import { useEffect, useState } from "react";
import { X, ClipboardList, Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui";
import { aiModelPricingApi } from "@/lib/aiModelPricing";

interface Props {
  tier: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Renders the per-tier human guide (Markdown) as a side panel modal.
 * Backed by `GET /api/admin/ai-model-pricing/pricing/guide?tier=…`.
 */
export function StepByStepGuide({ tier, open, onClose }: Props) {
  const [markdown, setMarkdown] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    aiModelPricingApi
      .guide(tier)
      .then(setMarkdown)
      .catch((e) => setError(e?.message ?? "Failed to load guide"))
      .finally(() => setLoading(false));
  }, [open, tier]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FFD60A]/15 text-[#FFD60A]">
            <ClipboardList className="h-4 w-4" />
          </span>
          <span>
            <span className="block text-[10px] font-mono uppercase tracking-wider text-[var(--color-tertiary-label)]">
              Push new price
            </span>
            <span className="block text-base font-bold tracking-tight text-white">
              Tier: {tier}
            </span>
          </span>
        </span>
      }
      width={560}
    >
      <div className="text-sm leading-relaxed text-white/90">
        {loading && (
          <div className="flex items-center gap-2 text-[var(--color-tertiary-label)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading guide…
          </div>
        )}
        {error && <p className="text-[#FF3377]">{error}</p>}
        {!loading && !error && (
          <pre className="whitespace-pre-wrap font-sans text-[13px] text-white/90 leading-relaxed">
            {markdown}
          </pre>
        )}
      </div>
    </Dialog>
  );
}
